import type { Location, NavigateFunction, To } from 'react-router-dom';
import { isCollectionScannerRoute } from '../components/layout/routeChrome';
import type { AppBackState } from '../hooks/useBackOrFallback';
import {
  requestScannerCameraForNavigation,
  stashScannerEntryStream,
} from './scannerEntryCamera';

/** Full-screen camera task — covers the stack like UIKit / RN `fullScreenModal`. */
export const SCAN_MODAL_PRESENTATION = 'fullScreenModal' as const;

export type ScanModalLocationState = AppBackState & {
  background: Location;
  presentation: typeof SCAN_MODAL_PRESENTATION;
};

export function isScanModalLocationState(value: unknown): value is ScanModalLocationState {
  if (!value || typeof value !== 'object') return false;
  const state = value as Partial<ScanModalLocationState>;
  if (state.presentation !== SCAN_MODAL_PRESENTATION) return false;
  if (state.appBack !== true) return false;
  const background = state.background;
  return Boolean(
    background &&
      typeof background === 'object' &&
      typeof (background as Location).pathname === 'string',
  );
}

/** Underlay location while `/collection/scan` is presented as a modal, else null. */
export function getScanModalBackground(location: Location): Location | null {
  if (!isCollectionScannerRoute(location.pathname)) return null;
  return isScanModalLocationState(location.state) ? location.state.background : null;
}

export function isScanModalPresentation(location: Location): boolean {
  return getScanModalBackground(location) != null;
}

export function scanModalState(location: Location, backTo?: To): ScanModalLocationState {
  return {
    appBack: true,
    backTo: backTo ?? {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    },
    background: location,
    presentation: SCAN_MODAL_PRESENTATION,
  };
}

/**
 * Open the scanner as a fullScreen modal over the current route.
 * Starts getUserMedia in the same turn as the tap (iOS requirement).
 */
export function navigateToCollectionScanner(
  navigate: NavigateFunction,
  location: Location,
  options?: { backTo?: To; primeCamera?: boolean },
): void {
  if (isCollectionScannerRoute(location.pathname)) return;

  if (options?.primeCamera !== false) {
    stashScannerEntryStream(requestScannerCameraForNavigation());
  }

  navigate('/collection/scan', {
    state: scanModalState(location, options?.backTo),
  });
}
